import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { proceduresApi } from '../api/procedures';
import { PROCEDURE_LABELS, PROCEDURE_TYPES } from '../constants/procedureTemplates';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CreateProcedureModal = ({
    open,
    onOpenChange,
    initialClientId = null,
    initialPortfolioId = null
}) => {
    const { token, user, tenant } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [clients, setClients] = useState([]);
    const [users, setUsers] = useState([]);

    // Form State
    const [formData, setFormData] = useState({
        client_id: initialClientId || '',
        procedure_type: '',
        title: '',
        responsible_user_id: user?.id || '',
        priority: 'medium',
        authority: 'IMPI',
        folder_id: initialPortfolioId || '', // Por si se requiere en futuro
        procedure_number: '' // Opcional
    });

    // Reset form when opening/closing or initialClientId changes
    useEffect(() => {
        if (open) {
            setFormData(prev => ({
                ...prev,
                client_id: initialClientId || '',
                folder_id: initialPortfolioId || '',
                // If switching from global to client detail, ensure we reset if needed
            }));
            fetchDependencies();
        }
    }, [open, initialClientId, initialPortfolioId]);

    const fetchDependencies = async () => {
        try {
            const [clientsRes, usersRes] = await Promise.all([
                axios.get(`${API}/clients`, { 
                    headers: { Authorization: `Bearer ${token}` },
                    params: { limit: 1000 }
                }),
                axios.get(`${API}/organization/users`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            
            const clientsData = (clientsRes.data && clientsRes.data.items) ? clientsRes.data.items : (Array.isArray(clientsRes.data) ? clientsRes.data : []);
            setClients(clientsData.filter(c => c.activo !== false));
            setUsers(usersRes.data.filter(u => u.activo !== false));
        } catch (error) {
            console.error('Error loading dependencies:', error);
            // Fallback for users if organization endpoint fails (legacy support)
            try {
                const usersLegacy = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
                setUsers(usersLegacy.data.filter(u => u.activo !== false));
            } catch (e) {
                toast.error('Error al cargar listas de selección');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.client_id || !formData.procedure_type || !formData.title || !formData.responsible_user_id) {
            toast.error('Por favor completa los campos requeridos');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                ...formData,
                status: 'draft'
            };

            const createdProcedure = await proceduresApi.createProcedure(token, payload);

            toast.success('Procedimiento creado exitosamente');
            onOpenChange(false);

            // Navigate to detail
            navigate(`/procedures/${createdProcedure.id}`);
        } catch (error) {
            console.error('Error creating procedure:', error);
            toast.error(error.response?.data?.detail || 'Error al crear procedimiento');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>Nuevo Procedimiento</DialogTitle>
                    <DialogDescription>
                        Crea un nuevo expediente legal.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                        {/* Client Select */}
                        <div className="space-y-2 col-span-2">
                            <Label htmlFor="client_id">Cliente <span className="text-red-500">*</span></Label>
                            <Select
                                value={formData.client_id}
                                onValueChange={(val) => setFormData({ ...formData, client_id: val })}
                                disabled={!!initialClientId || loading}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar cliente..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {clients.map(client => (
                                        <SelectItem key={client.id} value={client.id}>
                                            {client.nombre}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Title */}
                        <div className="space-y-2 col-span-2">
                            <Label htmlFor="title">Título del Asunto <span className="text-red-500">*</span></Label>
                            <Input
                                id="title"
                                placeholder="Ej. Oposición vs Marca X"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                disabled={loading}
                            />
                        </div>

                        {/* Procedure Type */}
                        <div className="space-y-2">
                            <Label htmlFor="procedure_type">Tipo de Trámite <span className="text-red-500">*</span></Label>
                            <Select
                                value={formData.procedure_type}
                                onValueChange={(val) => setFormData({ ...formData, procedure_type: val })}
                                disabled={loading}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar tipo..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(PROCEDURE_LABELS).map(([key, label]) => (
                                        <SelectItem key={key} value={key}>
                                            {label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Authority */}
                        <div className="space-y-2">
                            <Label htmlFor="authority">Autoridad</Label>
                            <Select
                                value={formData.authority}
                                onValueChange={(val) => setFormData({ ...formData, authority: val })}
                                disabled={loading}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar autoridad..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="IMPI">IMPI</SelectItem>
                                    <SelectItem value="TFJA">TFJA</SelectItem>
                                    <SelectItem value="COURT">Juzgado/Tribunal</SelectItem>
                                    <SelectItem value="OTHER">Otra</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Responsible User */}
                        <div className="space-y-2">
                            <Label htmlFor="responsible">Responsable <span className="text-red-500">*</span></Label>
                            <Select
                                value={formData.responsible_user_id}
                                onValueChange={(val) => setFormData({ ...formData, responsible_user_id: val })}
                                disabled={loading}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Asignar a..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {users.map(u => (
                                        <SelectItem key={u.id} value={u.id}>
                                            {u.nombre}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Priority */}
                        <div className="space-y-2">
                            <Label htmlFor="priority">Prioridad</Label>
                            <Select
                                value={formData.priority}
                                onValueChange={(val) => setFormData({ ...formData, priority: val })}
                                disabled={loading}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="low">Baja</SelectItem>
                                    <SelectItem value="medium">Media</SelectItem>
                                    <SelectItem value="high">Alta</SelectItem>
                                    <SelectItem value="urgent">Urgente</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Optional: Procedure Number */}
                        <div className="space-y-2 col-span-2">
                            <Label htmlFor="proc_number">No. Expediente / Oficio (Opcional)</Label>
                            <Input
                                id="proc_number"
                                placeholder="Ej. 1234567"
                                value={formData.procedure_number}
                                onChange={(e) => setFormData({ ...formData, procedure_number: e.target.value })}
                                disabled={loading}
                            />
                        </div>

                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading ? 'Creando...' : 'Crear Procedimiento'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
